import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TFormInputs } from '../../components/CreateReview/types';
import { DEFAULT_FINGER_PRINT_VALUE } from '../../constants/fingerPrint';
import { FILTER_NAMES, FILTERS } from '../../constants/reviews';
import { decorateReviews } from '../../helpers/decorators';
import type { ReviewType } from '../../helpers/reviews';
import { getReviewType } from '../../helpers/reviews';
import { IItem as IOrganizationItem } from '../../interfaces/organizations';
import {
  IAggregatedReviewsCount,
  IResolveBody,
  IReviewCreateBody,
  IReviewItem,
  IReviewsCountQuery,
  IReviewsSqueeze,
  IReviewsStats,
  IVoteBody,
  IVotes,
} from '../../interfaces/reviews';
import {
  createReview,
  deleteReview,
  getVotes,
  loadReviewsCount,
  loadReviewsList,
  resolveReview,
  updateReview,
  voteReview,
} from '../../services/reviews';
import { GlobalState } from '../index';
import { selectServerFilters } from '../selectors/filters';
import { selectReviewItem } from '../selectors/reviews';

export interface IReviewsListState {
  items: IReviewItem[];
  total: number;
  pageSize?: number;
  loading: boolean;
  isLoadingMore: boolean;
  loadingError: any;
}

interface IState {
  list: IReviewsListState;
  review: {
    item: IReviewItem;
    loading: boolean;
    loadingError: any;
  };
  postReview: {
    postReview?: string | boolean;
    loading?: boolean;
    loadingError?: any;
  };
  currentReviewType: ReviewType;
  aggregatedCount: IAggregatedReviewsCount;
  stats: IReviewsStats;
  listQuery: FILTERS;
  squeeze: IReviewsSqueeze;
}

const initialState: IState = {
  list: {
    items: [],
    total: 0,
    loading: false,
    isLoadingMore: false,
    loadingError: null,
  },
  review: {
    item: null,
    loading: false,
    loadingError: null,
  },
  postReview: {
    postReview: '',
    loading: false,
    loadingError: null,
  },
  currentReviewType: {} as ReviewType,
  aggregatedCount: {
    groupedByServices: [],
    groupedByRatingValue: [],
  },
  stats: {},
  listQuery: {},
  squeeze: {
    items: null,
    pageIndex: 0,
    pageSize: 0,
    total: 0,
  },
};

const name = 'reviews';

const slice = createSlice({
  name,
  initialState,
  reducers: {
    setCompanyCommentId: (state, { payload }: PayloadAction<{ companyId: number }>) => {
      state.review.item.commentCompanyId = payload.companyId;
    },
    setCurrentReviewType: (state, action: PayloadAction<IState['currentReviewType']>) => {
      state.currentReviewType = action.payload;
    },
    setReviewItem: (state, { payload }: PayloadAction<IReviewItem>) => {
      state.review.item = payload;
    },
    setReviewsList: (state, { payload }: PayloadAction<IReviewsListState>) => {
      state.list.items = payload.items;
      state.list.total = payload.total;
      state.list.pageSize = payload.pageSize;
    },
    setReviewsCount: (state, { payload }: PayloadAction<IAggregatedReviewsCount>) => {
      state.aggregatedCount = payload;
    },
    setReviewsStats: (state, { payload }: PayloadAction<IReviewsStats>) => {
      state.stats = payload;
    },
    setListQuery: (state, { payload }: PayloadAction<FILTERS>) => {
      state.listQuery = payload;
    },
    setReviewsSqueeze: (state, { payload }) => {
      state.squeeze = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewsList.fulfilled, (state, { payload }) => {
        state.list.items = payload.items;
        state.list.total = payload.total;
        state.list.pageSize = payload.pageSize;
        state.list.loading = false;
        state.list.isLoadingMore = false;
        state.list.loadingError = null;
      })
      .addCase(
        fetchReviewsList.pending,
        (
          state,
          {
            meta: {
              arg: { filter },
            },
          },
        ) => {
          state.list.loading = true;
          state.list.loadingError = null;
          state.list.isLoadingMore = Boolean(filter.page > 0);
        },
      )
      .addCase(fetchReviewsList.rejected, (state, { error }) => {
        state.list.loading = false;
        state.list.loadingError = error;
      })

      .addCase(doCreateReview.fulfilled, (state) => {
        state.postReview.postReview = true;
        state.postReview.loading = false;
        state.postReview.loadingError = null;
      })
      .addCase(doCreateReview.pending, (state) => {
        state.postReview.loading = true;
        state.postReview.loadingError = null;
      })
      .addCase(doCreateReview.rejected, (state, { error }) => {
        state.postReview.loading = false;
        state.postReview.loadingError = error;
        throw error;
      })

      .addCase(doUpdateReview.fulfilled, (state) => {
        state.postReview.loading = false;
      })
      .addCase(doUpdateReview.pending, (state) => {
        state.postReview.loading = true;
        state.postReview.loadingError = null;
      })
      .addCase(doUpdateReview.rejected, (state, { error }) => {
        state.postReview.loading = false;
        state.postReview.loadingError = error;
        throw error;
      })

      .addCase(doDeleteReview.fulfilled, (state, { payload: { id } }) => {
        state.review.item.reviewTag = '';
        state.list.items = state.list.items.filter((item) => item.id !== id);
      })
      .addCase(doDeleteReview.rejected, (_state, { error }) => {
        throw error;
      })

      .addCase(doResolveReview.fulfilled, (state) => {
        state.review.item.problemSolved = true;
      })
      .addCase(doResolveReview.rejected, (_state, { error }) => {
        throw error;
      })

      .addCase(doVoteReview.fulfilled, (state, { payload }) => {
        if (state.review.item) {
          state.review.item.votes = payload.votes;
          state.review.item.vote = payload.vote;
        }
      })
      .addCase(doVoteReview.rejected, () => {});
  },
});

const { actions, reducer } = slice;

export const {
  setCurrentReviewType,
  setCompanyCommentId,
  setReviewItem,
  setReviewsList,
  setReviewsCount,
  setReviewsStats,
  setListQuery,
  setReviewsSqueeze,
} = actions;

export const fetchReviewsList = createAsyncThunk<
  {
    items: IReviewItem[];
    total: number;
    pageSize: number;
  },
  { filter: FILTERS; organizations?: IOrganizationItem[]; organization?: IOrganizationItem; isWidget?: boolean },
  { state: GlobalState }
>(`${name}/fetchReviewsList`, async (payload, { getState }) => {
  const { filter, organizations, organization, isWidget } = payload;
  const isLoadingMore = filter.page > 0;

  // @ts-ignore
  const typeForService = getReviewType(filter.reviewObjectType)?.typeForService;
  const serverFilters = selectServerFilters(getState());
  const productTag = serverFilters?.[typeForService]?.tag?.find((tag) => tag.alias === filter.product)?.key;

  const tag = filter[FILTER_NAMES.PRODUCT] !== 'All' ? productTag : filter[FILTER_NAMES.PRODUCT];

  const fetchParams = {
    reviewObjectType: filter.reviewObjectType,
    reviewObjectId: filter.reviewObjectId,
    tag: tag !== 'All' ? tag : '',
    rated: filter.rated,
    orderBy: filter.orderBy,
    filterBy: filter.filterBy,
    pageIndex: !!+filter?.page ? +filter.page : 0,
    pageSize: filter.pageSize,
    locationGarId: filter[FILTER_NAMES.LOCATION_GAR_ID],
    specificProductId: filter.specificProduct !== 'All' ? filter.specificProduct : '',
    withVotes: true,
    fingerPrint: filter.fingerPrint,
    newIds: filter.newIds,
    from: filter.from,
    to: filter.to,
    rateStatus: filter.rateStatus,
    squeezesVectorIds: filter.squeezesVectorIds,
  };

  const reviews = await loadReviewsList(fetchParams);

  let totalReviews = reviews.total;

  if (isWidget) {
    const reviewsCountParams: IReviewsCountQuery = {
      organizationId: filter.reviewObjectId as string,
      organizationType: filter.organizationType,
    };

    totalReviews = await loadReviewsCount(reviewsCountParams);
  }

  const fp = getState().user.fp;
  const reviewIds = reviews.items.map((review) => review.id);

  const { data: votes } =
    reviewIds.length === 0
      ? { data: {} }
      : await getVotes({
          ReviewIds: reviewIds,
          Fingerprint: fp ?? DEFAULT_FINGER_PRINT_VALUE,
        });

  const reviewsList = decorateReviews({
    reviews,
    organizations,
    organization,
    votes,
  });

  const items = isLoadingMore ? [...getState().reviews.list.items, ...reviewsList.items] : reviewsList.items;

  return {
    items,
    total: totalReviews,
    pageSize: reviewsList.pageSize,
    isLoadingMore,
  };
});

export const doCreateReview = createAsyncThunk<number, IReviewCreateBody>(`${name}/doCreateReview`, async (payload) => {
  return await createReview(payload);
});

export const doUpdateReview = createAsyncThunk<
  void,
  {
    data: IReviewCreateBody;
    initData: TFormInputs;
  }
>(`${name}/doUpdateReview`, async (payload) => {
  await updateReview(payload);
});

export const doDeleteReview = createAsyncThunk<{ id: number }, { id: number }>(
  `${name}/doDeleteReview`,
  async (payload) => {
    const { id } = payload;

    await deleteReview(id);

    return { id };
  },
);

export const doResolveReview = createAsyncThunk<void, IResolveBody>(`${name}/doResolveReview`, async (payload) => {
  const { id, problemSolved } = payload;
  await resolveReview({ id, problemSolved });
});

export const doVoteReview = createAsyncThunk<
  {
    votes: IVotes;
    vote: boolean;
  },
  IVoteBody,
  { state: GlobalState }
>(`${name}/doVoteReview`, async (payload, { getState }) => {
  const { loading } = selectReviewItem(getState());

  if (!loading) {
    const { data } = await voteReview(payload);

    return {
      votes: data.votes,
      vote: data.vote,
    };
  }
});

export default reducer;
